import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Music, Briefcase, Check, Crown, Sparkles, Users, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

const CATEGORY_META = {
  dating: { icon: Heart, color: "#FF00FF", glow: "0 0 12px #FF00FF" },
  artist: { icon: Music, color: "#00FFFF", glow: "0 0 12px #00FFFF" },
  business: { icon: Briefcase, color: "#FFD700", glow: "0 0 12px #FFD700" },
};

export default function Plans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [active, setActive] = useState({ active_plans: [], subscriptions: {} });
  const [loadingPlan, setLoadingPlan] = useState(null);

  const load = useCallback(async () => {
    const [cRes, bRes, mRes] = await Promise.all([
      fetch(`${API}/plans/catalog`),
      fetch(`${API}/bundles/catalog`),
      user ? fetch(`${API}/plans/me`, { credentials: "include" }) : Promise.resolve(null),
    ]);
    if (cRes.ok) setPlans((await cRes.json()).plans);
    if (bRes.ok) setBundles((await bRes.json()).bundles);
    if (mRes && mRes.ok) setActive(await mRes.json());
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const subscribe = async (plan_id) => {
    if (!user) { navigate("/login"); return; }
    setLoadingPlan(plan_id);
    try {
      const r = await fetch(`${API}/recurring/subscribe`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: plan_id, origin_url: window.location.origin }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      window.location.href = d.url;
    } catch (e) { toast.error(e.message); setLoadingPlan(null); }
  };

  const subscribeBundle = async (bundle_id) => {
    if (!user) { navigate("/login"); return; }
    setLoadingPlan(bundle_id);
    try {
      const r = await fetch(`${API}/recurring/subscribe`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: bundle_id, origin_url: window.location.origin }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      window.location.href = d.url;
    } catch (e) { toast.error(e.message); setLoadingPlan(null); }
  };

  const openBillingPortal = async () => {
    try {
      const r = await fetch(`${API}/billing/portal`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      window.location.href = d.url;
    } catch (e) { toast.error(e.message); }
  };

  const grouped = plans.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #1a0033 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-6xl">
        <div className="text-center mb-8">
          <Crown className="w-14 h-14 mx-auto text-[#FFD700] mb-2" style={{ filter: "drop-shadow(0 0 15px #FFD700)" }} />
          <h1 className="text-3xl sm:text-4xl font-bold text-[#FFD700]" style={{ textShadow: "0 0 12px #FFD700" }} data-testid="plans-title">
            OurSpacez Subscriptions
          </h1>
          <p className="text-[#FF66FF] mt-2 max-w-2xl mx-auto">
            Pick what you need. Stack any plans. Friends & social are always FREE.
          </p>
          {active.active_plans.length > 0 && (
            <div className="flex items-center justify-center gap-3 flex-wrap mt-3">
              <p className="text-xs text-[#00FF00]" data-testid="active-plans-label">
                ✓ Active: {active.active_plans.map((p) => plans.find((x) => x.plan_id === p)?.label).filter(Boolean).join(", ")}
              </p>
              <button
                onClick={openBillingPortal}
                className="text-xs underline text-[#00FFFF] hover:text-white"
                data-testid="billing-portal-btn"
              >
                Manage / Cancel
              </button>
            </div>
          )}
          <p className="text-[10px] text-[#FFB3FF] mt-2 italic">
            🔁 Plans auto-renew monthly. Cancel anytime via "Manage".
          </p>
        </div>

        {/* Free tier card */}
        <div className="myspace-card mb-8 border-[#00FF00]" data-testid="free-tier-card">
          <div className="flex items-center gap-3 flex-wrap">
            <Users className="w-8 h-8 text-[#00FF00]" />
            <div className="flex-1 min-w-[200px]">
              <p className="text-lg font-bold text-[#00FF00]">Free — Social & Friends</p>
              <p className="text-xs text-[#99CCFF]">Profiles, Top 8, posts, chat, video feed, stories. Forever free.</p>
            </div>
            <span className="text-[#00FF00] font-bold text-2xl">$0</span>
          </div>
        </div>

        {/* Bundles section — multi-plan discount packs */}
        {bundles.length > 0 && (
          <section className="mb-10" data-testid="plans-section-bundles">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-6 h-6 text-[#FFD700]" style={{ filter: "drop-shadow(0 0 12px #FFD700)" }} />
              <h2 className="text-2xl font-bold uppercase text-[#FFD700]" style={{ textShadow: "0 0 12px #FFD700" }}>
                Bundles · Save up to $10/mo
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bundles.map((b) => {
                const allActive = b.plan_ids.every((pid) => active.active_plans.includes(pid));
                return (
                  <div key={b.bundle_id} className="myspace-card flex flex-col border-[#FFD700]" data-testid={`bundle-card-${b.bundle_id}`}>
                    <p className="text-xl font-bold text-[#FFD700]">{b.label}</p>
                    <p className="text-sm text-[#FF66FF] mb-2">{b.tagline}</p>
                    <div className="flex items-end gap-2 mb-2">
                      <span className="text-3xl font-bold text-[#FFD700]">${b.amount}</span>
                      <span className="text-sm text-[#99CCFF] line-through">${b.individual_total}</span>
                      <span className="text-xs text-[#00FF00] font-bold">{b.savings_label}</span>
                    </div>
                    <ul className="space-y-1 mb-3 flex-1">
                      {b.plan_ids.map((pid) => {
                        const p = plans.find((x) => x.plan_id === pid);
                        return (
                          <li key={pid} className="flex items-center gap-2 text-sm text-[#FF66FF]">
                            <Check className="w-4 h-4 text-[#FFD700] flex-shrink-0" />
                            <span>{p?.label || pid}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <button
                      onClick={() => subscribeBundle(b.bundle_id)}
                      disabled={loadingPlan === b.bundle_id}
                      className="myspace-btn-primary w-full disabled:opacity-50"
                      data-testid={`bundle-subscribe-btn-${b.bundle_id}`}
                    >
                      {loadingPlan === b.bundle_id ? "Redirecting..." : allActive ? "Extend bundle (30d)" : `Get bundle — $${b.amount}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {Object.entries(grouped).map(([cat, list]) => {
          const meta = CATEGORY_META[cat] || { icon: Sparkles, color: "#FFFFFF", glow: "" };
          const Icon = meta.icon;
          return (
            <section key={cat} className="mb-8" data-testid={`plans-section-${cat}`}>
              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-6 h-6" style={{ color: meta.color, filter: `drop-shadow(${meta.glow})` }} />
                <h2 className="text-2xl font-bold uppercase" style={{ color: meta.color, textShadow: meta.glow }}>
                  {cat}
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {list.map((p) => {
                  const isActive = active.active_plans.includes(p.plan_id);
                  return (
                    <div
                      key={p.plan_id}
                      className="myspace-card flex flex-col"
                      style={{ borderColor: isActive ? "#00FF00" : meta.color }}
                      data-testid={`plan-card-${p.plan_id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xl font-bold text-[#00FFFF]">{p.label}</p>
                        {isActive && <span className="text-xs text-[#00FF00] border border-[#00FF00] px-2 py-0.5 rounded">ACTIVE</span>}
                      </div>
                      <p className="text-sm text-[#FF66FF] mb-3">{p.tagline}</p>
                      <p className="text-3xl font-bold mb-3" style={{ color: meta.color }}>
                        ${p.amount}<span className="text-sm text-[#99CCFF] font-normal">/30 days</span>
                      </p>
                      <ul className="space-y-1.5 mb-4 flex-1">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-[#FF66FF]">
                            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: meta.color }} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => subscribe(p.plan_id)}
                        disabled={loadingPlan === p.plan_id}
                        className="myspace-btn-primary w-full disabled:opacity-50"
                        data-testid={`subscribe-btn-${p.plan_id}`}
                      >
                        {loadingPlan === p.plan_id ? "Redirecting..." : isActive ? "Extend (30 days)" : `Subscribe — $${p.amount}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <p className="text-xs text-[#99CCFF] text-center mt-8">
          Plans renew via 30-day repeat checkout (manual). All payments secured by Stripe.
        </p>
      </main>
    </div>
  );
}

export function PlansSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [planLabel, setPlanLabel] = useState("");

  const poll = useCallback(async (sid, attempt) => {
    if (attempt >= 8) { setStatus("timeout"); return; }
    try {
      const r = await fetch(`${API}/plans/status/${sid}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        if (d.payment_status === "paid") {
          setStatus("paid");
          setPlanLabel(d.plan_id || "your plan");
          if (refreshUser) await refreshUser();
          return;
        }
        if (d.status === "expired") { setStatus("expired"); return; }
      }
    } catch (e) { /* retry */ }
    setTimeout(() => poll(sid, attempt + 1), 2000);
  }, [refreshUser]);

  useEffect(() => { if (sessionId) poll(sessionId, 0); }, [sessionId, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #1a0033 0%, #000022 100%)" }}>
      <div className="myspace-card text-center max-w-md w-full">
        {status === "checking" && (<><div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-[#FFD700]">Activating your plan...</p></>)}
        {status === "paid" && (<>
          <Crown className="w-20 h-20 mx-auto text-[#FFD700] mb-4" style={{ filter: "drop-shadow(0 0 20px #FFD700)" }} />
          <h2 className="text-3xl font-bold text-[#FFD700] mb-2" data-testid="plan-success-title">Plan Activated!</h2>
          <p className="text-[#00FFFF] mb-6">{planLabel.replaceAll("_", " ")} is now active for 30 days.</p>
          <button onClick={() => navigate("/plans")} className="myspace-btn-primary mr-2" data-testid="plan-success-back-btn">View plans</button>
          <button onClick={() => navigate("/dashboard")} className="myspace-btn">Dashboard</button>
        </>)}
        {(status === "timeout" || status === "expired") && (<><p className="text-[#FF6600] text-lg font-bold mb-4">Payment could not be verified</p><button onClick={() => navigate("/plans")} className="myspace-btn">Back to plans</button></>)}
      </div>
    </div>
  );
}
